import type { HubCourse, HubRow, PartRun, RowRun } from "./hubTypes";

export type PartStatus = "none" | "pending" | "running" | "done" | "blocked";

export interface PartState {
  status: PartStatus;
  message: string | null;
}

export interface RowState {
  // Row-level reasons nothing in the row may be uploaded.
  blocked: string[];
  doc: PartState;
  quiz: PartState;
}

export type PartDisplay = PartStatus | PartRun;

export interface RowView {
  doc: PartDisplay;
  quiz: PartDisplay;
  messages: string[];
  // Written to the tracker like the messages, but not errors: they never
  // change a part's state nor what the counters say.
  notices: string[];
}

export type ReadyRow = HubRow & { courseId: number; course: HubCourse };

// “ ” « » become ", and ‘ ’ become '.
const DOUBLE_QUOTES = /[\u201C\u201D\u00AB\u00BB]/g;
const SINGLE_QUOTES = /[\u2018\u2019]/g;

export const normalizeTitle = (title: string): string =>
  title
    .normalize("NFC")
    .toLowerCase()
    .replace(DOUBLE_QUOTES, '"')
    .replace(SINGLE_QUOTES, "'")
    .replace(/\s+/g, " ")
    .trim();

export const normalizeClave = (clave: string): string =>
  clave.trim().toUpperCase();

export const prefixed = (prefix: string, messages: string[]): string[] =>
  messages.map((message) => `${prefix}: ${message}`);

// A message is one line of the tracker's "errores" cell. A line break inside it
// would leave a line with no "Doc:", "Quiz:" or "Aviso:" mark to read it back by.
export const oneLine = (text: string): string =>
  text.trim().replace(/\s*[\r\n]\s*/g, " ");

const NOTICE_MARK = "Aviso: ";
const NOTICE_LINE = new RegExp(`^(?:Doc|Quiz): ${NOTICE_MARK}`);

// The tracker's "errores" cell holds errors and notices together, so a notice
// carries a mark to be told apart when a row is read back from it.
export const asNotice = (text: string): string => `${NOTICE_MARK}${text}`;

export function rowBlockers(row: HubRow): string[] {
  if (row.rowIssues.length > 0) return row.rowIssues;
  if (row.courseId === null) return ["El ID del curso no es un número."];
  if (row.course === null) {
    return [`El curso ${row.courseId} no existe o está eliminado.`];
  }
  return [];
}

// A row with no blockers always has its course ID and its course.
export const isReadyRow = (row: HubRow): row is ReadyRow =>
  rowBlockers(row).length === 0;

const NONE: PartState = { status: "none", message: null };
const PENDING: PartState = { status: "pending", message: null };
const RUNNING: PartState = { status: "running", message: null };
const DONE: PartState = { status: "done", message: null };

export interface StateOptions {
  // «Volver a subir lo ya subido»: what is on the platform is uploaded again,
  // in place, so being done no longer settles a part.
  reupload?: boolean;
}

// A part without a link is absent. For a part with one, a checked box is the
// content team's word that it is on the platform (even a quiz ticked by hand
// after a manual upload): it wins over every other rule and is never re-run.
function docState(row: HubRow, { reupload }: StateOptions): PartState {
  if (!row.documentId) return NONE;
  const job = row.docJob;
  const ingesting =
    job !== null && job.status !== "COMPLETED" && job.status !== "FAILED";
  // A job still ingesting would write next to the upload; it is left alone.
  if (reupload) return ingesting ? RUNNING : PENDING;
  if (row.docUploaded) return DONE;
  if (job?.status === "COMPLETED") return DONE;
  if (ingesting) return RUNNING;
  return { status: "pending", message: job?.errorMessage ?? null };
}

// SCRUM-1532: a quiz the course already holds is replaced in place, so it no
// longer blocks the part nor stands for it; only the box says it is done.
function quizState(row: HubRow, { reupload }: StateOptions): PartState {
  if (!row.yamlFileId) return NONE;
  if (row.quizUploaded && !reupload) return DONE;
  return PENDING;
}

export function deriveRowState(
  row: HubRow,
  options: StateOptions = {},
): RowState {
  return {
    blocked: rowBlockers(row),
    doc: docState(row, options),
    quiz: quizState(row, options),
  };
}

// What the tracker already says only matters when nothing fresher is known.
function derivedText(
  row: HubRow,
  state: RowState,
): { messages: string[]; notices: string[] } {
  const fresh = [
    ...state.blocked,
    ...prefixed("Doc", state.doc.message ? [state.doc.message] : []),
    ...prefixed("Quiz", state.quiz.message ? [state.quiz.message] : []),
  ].map(oneLine);
  if (fresh.length > 0) return { messages: fresh, notices: [] };
  const lines = (row.currentError ?? "")
    .split(/\r?\n/)
    .filter((line) => line.trim() !== "");
  return {
    messages: lines.filter((line) => !NOTICE_LINE.test(line)),
    notices: lines.filter((line) => NOTICE_LINE.test(line)),
  };
}

// A blocked row uploads nothing, so what would have been pending is blocked.
const displayStatus = (part: PartState, rowBlocked: boolean): PartDisplay =>
  rowBlocked && part.status === "pending" ? "blocked" : part.status;

export function rowView(
  row: HubRow,
  run?: RowRun,
  options: StateOptions = {},
): RowView {
  const state = deriveRowState(row, options);
  const rowBlocked = state.blocked.length > 0;
  const doc = run?.doc ?? displayStatus(state.doc, rowBlocked);
  const quiz = run?.quiz ?? displayStatus(state.quiz, rowBlocked);
  const { messages, notices } =
    run?.messages !== undefined
      ? { messages: run.messages, notices: run.notices ?? [] }
      : derivedText(row, state);
  return { doc, quiz, messages, notices };
}

export interface PartCounts {
  pending: number;
  uploaded: number;
  withError: number;
}

const OTHER_PART = { Doc: "Quiz", Quiz: "Doc" } as const;

// What a part answers for: the lines marked with its name and the lines marked
// with neither, which are the row's.
const errorsOf = (part: keyof typeof OTHER_PART, messages: string[]) =>
  messages.filter((line) => !line.startsWith(`${OTHER_PART[part]}: `));

function countPart(
  counts: PartCounts,
  display: PartDisplay,
  errors: string[],
): void {
  if (display === "none") return;
  if (display === "done") counts.uploaded += 1;
  else if (display === "error" || errors.length > 0) counts.withError += 1;
  else counts.pending += 1;
}

// Each part counts on its own, so a row whose OD is uploaded and whose quiz
// failed is one of each. A part the row has no link for counts nowhere, and a
// part that is done is never an error, whatever the tracker still says.
export function summarizeRows(views: RowView[]) {
  const doc: PartCounts = { pending: 0, uploaded: 0, withError: 0 };
  const quiz: PartCounts = { pending: 0, uploaded: 0, withError: 0 };
  views.forEach((view) => {
    countPart(doc, view.doc, errorsOf("Doc", view.messages));
    countPart(quiz, view.quiz, errorsOf("Quiz", view.messages));
  });
  return { doc, quiz };
}
